import React, { useState, useEffect } from 'react';
import { setCredentials, getCredentials, isConfigured } from '../services/datatraceApi';
import './DataTraceConfig.css';

function DataTraceConfig({ onConfigured, onBack }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [scope, setScope] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Load existing credentials if any
    const creds = getCredentials();
    if (creds.clientId) setClientId(creds.clientId);
    if (creds.scope) setScope(creds.scope);
    // Set partial secret as placeholder hint
    if (creds.partialSecret) {
      setClientSecret('');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    setTokenInfo(null);

    try {
      // Get partial secret from credentials and complete it with user input
      const creds = getCredentials();
      const fullSecret = creds.partialSecret ? creds.partialSecret + clientSecret : clientSecret;

      // Save credentials
      const finalScope = scope || `${clientId}/.default`;
      setCredentials(clientId, fullSecret, finalScope);

      // Fetch bearer token for display
      const response = await fetch('/api/datatrace/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credentials: {
            clientId,
            clientSecret: fullSecret,
            scope: finalScope
          }
        })
      });

      const data = await response.json();

      if (data.success && data.token) {
        setTokenInfo(data);
      } else {
        setError(data.error || 'Failed to obtain token');
        return;
      }

      // Don't auto-navigate - let user see the token first
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (tokenInfo?.token) {
      navigator.clipboard.writeText(tokenInfo.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyAuthHeader = () => {
    if (tokenInfo?.usage) {
      navigator.clipboard.writeText(tokenInfo.usage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleContinue = () => {
    onConfigured();
  };

  const handleSkip = () => {
    // Allow using mock data if no credentials
    onConfigured(true); // true = use mock data
  };

  return (
    <div className="datatrace-config">
      <div className="config-header">
        <div>
          <h2>Configure DataTrace API</h2>
          <p className="config-subtitle">Enter your DataTrace Digital Gateway credentials</p>
        </div>
      </div>

      <div className="config-container">
        <div className="config-info">
          <div className="info-icon">🔐</div>
          <p>
            DataTrace API credentials are pre-configured. For security, you only need to enter the last 3 characters of the secret key.
            Your completed credentials are stored only in browser memory and are not persisted.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="config-form">
          <div className="form-group">
            <label htmlFor="clientId">Client ID</label>
            <input
              type="text"
              id="clientId"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Pre-configured"
              required
              disabled
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
            <p className="input-hint">Pre-configured: {clientId}</p>
          </div>

          <div className="form-group">
            <label htmlFor="clientSecret">Client Secret - Last 3 Characters</label>
            <div className="secret-input-container">
              <input
                type={showSecret ? 'text' : 'password'}
                id="clientSecret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Enter last 3 characters only"
                required
                maxLength="3"
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? '🙈' : '👁️'}
              </button>
            </div>
            <p className="input-hint">Partial key: bHR8Q~yJ6SQsxQd2W4AsKQbCuqvv.d27be9Y5*** (enter the last 3 characters)</p>
          </div>

          <div className="form-group">
            <label htmlFor="scope">Scope</label>
            <input
              type="text"
              id="scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="Pre-configured"
              disabled
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
            <p className="input-hint">Pre-configured: {scope}</p>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="skip-button"
              onClick={handleSkip}
            >
              Use Demo Data
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isLoading || !clientSecret || clientSecret.length !== 3}
            >
              {isLoading ? 'Activating DataTrace...' : 'Activate DataTrace API'}
            </button>
          </div>
        </form>

        {/* Token Display Section */}
        {tokenInfo && (
          <div className="token-display">
            <div className="token-header">
              <h4>Bearer Token Retrieved Successfully</h4>
              <span className="token-expires">
                Expires: {tokenInfo.expiresAt ? new Date(tokenInfo.expiresAt).toLocaleTimeString() : 'Unknown'}
              </span>
            </div>

            <div className="token-field">
              <label>Bearer Token:</label>
              <div className="token-value-container">
                <code className="token-value">
                  {showToken ? tokenInfo.token : `${tokenInfo.token.substring(0, 50)}...`}
                </code>
                <div className="token-actions">
                  <button
                    type="button"
                    className="token-action-btn"
                    onClick={() => setShowToken(!showToken)}
                  >
                    {showToken ? 'Hide' : 'Show Full'}
                  </button>
                  <button
                    type="button"
                    className="token-action-btn"
                    onClick={handleCopyToken}
                  >
                    {copied ? 'Copied!' : 'Copy Token'}
                  </button>
                </div>
              </div>
            </div>

            <div className="token-field">
              <label>Authorization Header (for Postman):</label>
              <div className="token-value-container">
                <code className="token-value auth-header">{tokenInfo.usage}</code>
                <button
                  type="button"
                  className="token-action-btn"
                  onClick={handleCopyAuthHeader}
                >
                  {copied ? 'Copied!' : 'Copy Header'}
                </button>
              </div>
            </div>

            <div className="postman-info">
              <h5>Postman Setup:</h5>
              <ol>
                <li>Method: <strong>POST</strong></li>
                <li>URL: <code>https://uat.gateway.datatracetitle.com/ddg/property_data_sync</code></li>
                <li>Headers:
                  <ul>
                    <li><code>Authorization: Bearer [token]</code></li>
                    <li><code>Content-Type: application/json</code></li>
                  </ul>
                </li>
                <li>Body: Use the request JSON from server console logs</li>
              </ol>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="submit-button"
                onClick={handleContinue}
              >
                Continue to Search
              </button>
            </div>
          </div>
        )}

        <div className="auth-info">
          <h4>Authentication Details</h4>
          <p>Token endpoint:</p>
          <code>https://login.microsoftonline.com/4cc65fd6-9c76-4871-a542-eb12a5a7800c/oauth2/v2.0/token</code>
          <p>API endpoint:</p>
          <code>https://ddg-azfn-traffic-orchestrator-uat.dbse-p-1-dbse-ae-1.appserviceenvironment.net</code>
        </div>
      </div>
    </div>
  );
}

export default DataTraceConfig;
